import ToggleSwitch from "@/components/atoms/ToggleSwitch/ToggleSwitch";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import { isoBandsLayerConfig, isoLinesLayerConfig } from "@/constants/layerConfigAttributes";
import { FeatureHubGuard } from "@/featureFlags";
import type { LayerUnion } from "@/layers";
import { reconcileIsoBandFillColors, resolveIsoBandEntries } from "@/layers/geojson/isoBandsFillUtils";
import { type IsoBandsLayer, readIsoLayerCustomOptions } from "@/layers/geojson/isoLayerShared";
import { useDesktop } from "@/overlay/components";
import { IntegerInput } from "@/overlay/components/CommonParts/CustomInput";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import {
  CalibratedToggle,
  ColorPickerInput,
  OpacityInput,
} from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { WindowKind, instanceId } from "@/overlay/components/window";
import { Modal } from "@/overlay/components/window/Modal";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import type { MapId } from "@/types/type-utils";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import { transformEnsSelectStringToArray } from "@/utility/ensemble";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useRef, useState } from "react";
import { IsoBandsFillColorsEditor } from "./IsoBandsFillColorsEditor";
import { BufferedTextInput } from "./IsoBufferedTextInput";
import {
  type IsoInputField,
  asObject,
  buildIsoSelectionPatch,
  getInitialIsoInputField,
  normalizeIsoInput,
  toInputValue,
} from "./IsoLayerFormUtils";
import { IsoSelectionTypeSwitch } from "./IsoSelectionTypeSwitch";

export function IsoBandsLayerForm(props: {
  layer: IsoBandsLayer;
  mapId: MapId;
  changeProps: (propsValue: Partial<LayerUnion>) => void;
  onEditLayerParam: () => void;
}) {
  const { layer, mapId, changeProps, onEditLayerParam } = props;
  const desktop = useDesktop();
  const hideStyleInputs = transformEnsSelectStringToArray(layer.ens_select ?? "").length > 1;
  const customOptions = readIsoLayerCustomOptions(layer.custom_options);
  const [selectedField, setSelectedField] = useState<IsoInputField | null>(() =>
    getInitialIsoInputField({
      withIsobandIntervals: true,
      values: layer.values,
      valueRange: layer.value_range,
      isobandIntervals: customOptions.isoband_intervals,
    }),
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [showFillColorsEditor, setShowFillColorsEditor] = useState(false);
  const hasBandEntries =
    resolveIsoBandEntries({
      values: layer.values,
      valueRange: layer.value_range,
      isobandIntervals: customOptions.isoband_intervals,
    }).length > 0;

  const focusSelectedInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const applySelectedField = (field: IsoInputField) => {
    setSelectedField(field);
    focusSelectedInput();
  };

  const getActiveSelectionValue = (field: IsoInputField | null) => {
    if (field === null) {
      return null;
    }
    if (field === "values") {
      return layer.values;
    }
    if (field === "value_range") {
      return layer.value_range;
    }
    return customOptions.isoband_intervals;
  };

  const commitSelectionChange = (field: IsoInputField, rawValue: string) => {
    const selectionPatch = buildIsoSelectionPatch({
      field,
      rawValue,
      withIsobandIntervals: true,
      currentCustomOptions: layer.custom_options,
    }) as Partial<IsoBandsLayer>;
    const mergedCustomOptions: IsoBandsLayer["custom_options"] = {
      ...asObject(layer.custom_options),
      ...asObject(selectionPatch.custom_options),
      fill_colors: reconcileIsoBandFillColors({
        previous: {
          values: layer.values,
          valueRange: layer.value_range,
          isobandIntervals: customOptions.isoband_intervals,
        },
        next: {
          values: "values" in selectionPatch ? (selectionPatch.values as string | null | undefined) : layer.values,
          valueRange:
            "value_range" in selectionPatch
              ? (selectionPatch.value_range as string | null | undefined)
              : layer.value_range,
          isobandIntervals: asObject(selectionPatch.custom_options).isoband_intervals as string | null | undefined,
        },
        baseColor: customOptions.fill_color ?? isoBandsLayerConfig.defaultFillColor,
        fillColors: customOptions.fill_colors,
        fillColorMode: customOptions.fill_color_mode,
      }),
    };

    const nextPatch: Partial<IsoBandsLayer> = {
      ...selectionPatch,
      custom_options: mergedCustomOptions,
    };

    changeProps(nextPatch);
  };

  const handleSelectField = (field: IsoInputField) => {
    if (selectedField !== null && field === selectedField) {
      focusSelectedInput();
      return;
    }

    if (!normalizeIsoInput(getActiveSelectionValue(selectedField) ?? "")) {
      applySelectedField(field);
      return;
    }

    desktop.dispatch({
      type: "confirmationWindow",
      windowId: instanceId(WindowKind.ConfirmationWindow),
      customProps: {
        message: t`Values will be reset. Confirming this action will reset the values of isobands.`,
        promptType: "warning",
        callback: (confirmed: boolean) => {
          if (!confirmed) {
            return;
          }

          changeProps(
            buildIsoSelectionPatch({
              field,
              rawValue: "",
              withIsobandIntervals: true,
              currentCustomOptions: layer.custom_options,
            }),
          );
          applySelectedField(field);
        },
      },
    });
  };

  return (
    <>
      <ModelAndWeatherParameterForm layer={layer} mapId={mapId} onEditParameter={onEditLayerParam} />
      <OpacityInput
        layer={layer}
        change={changeProps}
        onStart={() => batchGroupBy.start()}
        onEnd={() => batchGroupBy.end()}
      />
      {hideStyleInputs || (
        <ColorPickerInput
          label={t`Text Color`}
          change={(color) => changeProps({ text_color: color })}
          value={layer.text_color}
        />
      )}
      <FormField label={t`Text Size`}>
        <IntegerInput
          {...isoLinesLayerConfig.textSizeLimits}
          onChange={(value) => changeProps({ text_size: value })}
          value={layer.text_size || 0}
        />
      </FormField>
      {hideStyleInputs || (
        <ColorPickerInput
          label={t`Line Color`}
          change={(color) => changeProps({ line_color: color })}
          value={layer.line_color}
        />
      )}
      <FormField label={t`Line Width`}>
        <IntegerInput
          {...isoLinesLayerConfig.lineWidthLimits}
          onChange={(value) => changeProps({ line_width: value })}
          value={layer.line_width || 0}
        />
      </FormField>
      <FormField label={t`Show values`}>
        <ToggleSwitch
          checked={customOptions.show_values !== false}
          onChange={(enabled) =>
            changeProps({
              custom_options: {
                ...asObject(layer.custom_options),
                show_values: enabled,
              },
            })
          }
        />
      </FormField>

      <IsoSelectionTypeSwitch selectedField={selectedField} onSelect={handleSelectField} withIsobandIntervals={true} />
      {selectedField === null && (
        <FormField label={t`Selection`} noSemanticLinking={true}>
          <div style={{ fontSize: "0.9rem", color: "var(--color-gray-700)" }}>
            <span>{t`Select Values, Ranges, or Intervals to define isobands.`}</span>
          </div>
        </FormField>
      )}
      {selectedField === "values" && (
        <FormField label={<span>{t`Values`}</span>}>
          <BufferedTextInput
            ref={inputRef}
            type="text"
            placeholder={t`e.g. 0,0.5,1`}
            onCommit={(rawValue) => commitSelectionChange("values", rawValue)}
            value={toInputValue(layer.values)}
          />
        </FormField>
      )}
      {selectedField === "value_range" && (
        <FormField label={<span>{t`Range`}</span>}>
          <BufferedTextInput
            ref={inputRef}
            type="text"
            placeholder={t`e.g. -40,40,5`}
            onCommit={(rawValue) => commitSelectionChange("value_range", rawValue)}
            value={toInputValue(layer.value_range)}
          />
        </FormField>
      )}
      <FeatureHubGuard flag="feature.isoband_intervals">
        {selectedField === "isoband_intervals" && (
          <FormField label={<span>{t`Intervals`}</span>}>
            <BufferedTextInput
              ref={inputRef}
              type="text"
              placeholder={t`e.g. 0,1000_2000,3000`}
              onCommit={(rawValue) => commitSelectionChange("isoband_intervals", rawValue)}
              value={toInputValue(customOptions.isoband_intervals)}
            />
          </FormField>
        )}
      </FeatureHubGuard>
      <FormField label={t`Colors`}>
        <Button
          icon="folder_open"
          onClick={() => setShowFillColorsEditor(true)}
          disabled={!hasBandEntries}
          data-testid="IsoBandsLayerForm-manage-fill-colors"
        >
          <Trans>Manage</Trans>
        </Button>
      </FormField>
      <Modal isOpen={showFillColorsEditor} onClose={() => setShowFillColorsEditor(false)} closeOnBackdropClick={false}>
        <IsoBandsFillColorsEditor
          layer={layer}
          onSave={(nextCustomOptions) =>
            changeProps({
              custom_options: {
                ...asObject(layer.custom_options),
                ...nextCustomOptions,
              },
            })
          }
          onClose={() => setShowFillColorsEditor(false)}
        />
      </Modal>
      <FeatureHubGuard flag="feature.isobands_fixed_bbox">
        <FormField label={t`Fixed BBox`}>
          <BufferedTextInput
            type="text"
            placeholder={"LngMin,LatMin,LngMax,LatMax"}
            onCommit={(rawValue) =>
              changeProps({
                custom_options: {
                  ...asObject(layer.custom_options),
                  fixed_bbox: normalizeIsoInput(rawValue),
                },
              })
            }
            value={toInputValue(customOptions.fixed_bbox)}
          />
        </FormField>
      </FeatureHubGuard>
      <FeatureHubGuard flag="feature.isobands_resolution">
        <FormField label={t`Resolution`}>
          <BufferedTextInput
            type="text"
            placeholder={"0.05,0.05 or 400x500"}
            onCommit={(rawValue) =>
              changeProps({
                custom_options: {
                  ...asObject(layer.custom_options),
                  resolution: normalizeIsoInput(rawValue),
                },
              })
            }
            value={toInputValue(customOptions.resolution)}
          />
        </FormField>
      </FeatureHubGuard>
      <FeatureHubGuard flag="api_url_suffix">
        <InitDateField
          label={t`API URL suffix`}
          defaultValue={(customOptions as { api_url_suffix?: string }).api_url_suffix ?? ""}
          onChange={(apiUrlSuffix) =>
            changeProps({
              custom_options: {
                ...asObject(layer.custom_options),
                api_url_suffix: apiUrlSuffix,
              },
            })
          }
        />
      </FeatureHubGuard>

      <FormField label={t`Median filter`}>
        <IntegerInput
          {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
          onChange={(value) => changeProps({ filter_median: value })}
          value={layer.filter_median ?? isoLinesLayerConfig.defaultMedian}
        />
      </FormField>
      <FormField label={t`Gaussian filter`}>
        <IntegerInput
          {...isoLinesLayerConfig.gaussianAndMedianFilterLimits}
          onChange={(value) => changeProps({ filter_gauss: value })}
          value={layer.filter_gauss ?? isoLinesLayerConfig.defaultGaussian}
        />
      </FormField>
      {hasCalibratedAvailable(layer.parameter_unit) && (
        <CalibratedToggle value={!!layer.calibrated} change={changeProps} />
      )}
    </>
  );
}
